import { useEffect, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Check, ChevronLeft, ChevronRight, Loader2, Sparkles, Trash2, X } from 'lucide-react';
import type { Company } from '../services/api';
import { captureSupportScope, isSupportActivity } from '../admin/supportTransport';
import { useSubscriptionGuard } from '../hooks/useSubscriptionGuard';
import UpgradeModal from '../components/UpgradeModal';
import { organizationApi, OrganizationApiError } from './api';
import type {
  OrganizationWorkspace,
  OrganizationalGenerationAnswers,
  StructurePreview,
  StructureApplyRequest,
  StructureApplyResult,
  StructureProposal,
} from './model';
import { proposalCount } from './model';
import {
  proposalDuplicates,
  proposalProblems,
  replaceProposalReference,
  type ProposalKind,
} from './proposal';

export interface StructureWizardProps {
  companyId: string;
  company: Company;
  workspace: OrganizationWorkspace;
  onApplied: (result: StructureApplyResult) => void | Promise<void>;
  onClose: () => void;
  onRefresh?: () => Promise<unknown>;
}

interface WizardDraft {
  step: number;
  answers: OrganizationalGenerationAnswers;
  preview: StructurePreview | null;
  pendingApply: StructureApplyRequest | null;
}

function isCachedProposal(value: unknown): value is StructureProposal {
  if (!value || typeof value !== 'object') return false;
  const proposal = value as StructureProposal;
  return ['divisions', 'teams', 'positions'].every((kind) => {
    const rows = proposal[kind as keyof StructureProposal];
    return (
      Array.isArray(rows) &&
      rows.every(
        (row) =>
          row &&
          typeof row.id === 'string' &&
          (('name' in row && typeof row.name === 'string') ||
            ('title' in row && typeof row.title === 'string'))
      )
    );
  });
}

const functionalAreas = [
  ['operations', 'Operations'],
  ['sales', 'Sales'],
  ['marketing', 'Marketing'],
  ['engineering', 'Engineering'],
  ['product', 'Product'],
  ['design', 'Design'],
  ['finance', 'Finance'],
  ['hr', 'People'],
  ['legal', 'Legal & compliance'],
  ['customer-success', 'Customer success'],
];
const customerSegments = [
  ['b2b-enterprise', 'Enterprise'],
  ['b2b-smb', 'Small businesses'],
  ['b2c-consumer', 'Consumers'],
  ['b2c-prosumer', 'Professionals'],
];
const defaultAnswers = (company: Company): OrganizationalGenerationAnswers => ({
  primaryFocus: '',
  companyStage:
    ({ small: 'startup', medium: 'scale-up', large: 'enterprise' } as Record<string, string>)[
      company.size
    ] || '',
  geographicalScope: '',
  functionalAreas: [],
  projectBasedWork: false,
  regulatoryCompliance: false,
  customerSegments: [],
  additionalContext: '',
});

function storageKey(companyId: string) {
  const clerk = (window as any).Clerk;
  return `stratplan:structure-builder:v1:${clerk?.user?.id || 'local'}:${clerk?.session?.id || 'local'}:${companyId}:${captureSupportScope()}`;
}

export default function StructureWizard({
  companyId,
  company,
  workspace,
  onApplied,
  onClose,
  onRefresh,
}: StructureWizardProps) {
  const key = storageKey(companyId);
  const [draft, setDraft] = useState<WizardDraft>(() => {
    if (!isSupportActivity()) {
      try {
        const cached = JSON.parse(sessionStorage.getItem(key) || 'null');
        if (
          cached?.answers &&
          typeof cached.step === 'number' &&
          cached.step >= 0 &&
          cached.step <= 2 &&
          Array.isArray(cached.answers.functionalAreas) &&
          Array.isArray(cached.answers.customerSegments) &&
          (!cached.preview ||
            (isCachedProposal(cached.preview.proposal) &&
              typeof cached.preview.baseRevision === 'number' &&
              Array.isArray(cached.preview.recommendations))) &&
          (!cached.pendingApply ||
            (cached.preview &&
              typeof cached.pendingApply.operationId === 'string' &&
              typeof cached.pendingApply.expectedRevision === 'number' &&
              isCachedProposal(cached.pendingApply.proposal)))
        )
          return {
            step: cached.step,
            answers: { ...defaultAnswers(company), ...cached.answers },
            preview: cached.preview || null,
            pendingApply: cached.pendingApply || null,
          };
      } catch {
        /* An unavailable cache does not prevent using the builder. */
      }
    }
    return { step: 0, answers: defaultAnswers(company), preview: null, pendingApply: null };
  });
  const [busy, setBusy] = useState<'preview' | 'apply' | null>(null);
  const inFlight = useRef(false);
  const alive = useRef(true);
  const currentKey = useRef(key);
  currentKey.current = key;
  const previewToken = useRef(0);
  const previewController = useRef<AbortController | null>(null);
  const [error, setError] = useState('');
  const [conflict, setConflict] = useState(false);
  const [contextConflict, setContextConflict] = useState(false);
  const [note, setNote] = useState('');
  const { withSubscriptionGuard, upgradeModalProps } = useSubscriptionGuard({
    companyId,
    feature: 'AI structure generation',
    title: 'Generate structure with Pro',
    description:
      'Pro is required to generate organizational suggestions. You can build your structure manually using Add.',
  });
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      previewToken.current += 1;
      previewController.current?.abort();
    };
  }, []);
  useEffect(() => {
    if (isSupportActivity()) return;
    try {
      sessionStorage.setItem(key, JSON.stringify(draft));
    } catch {
      /* Retain the in-memory draft. */
    }
  }, [draft, key]);

  const answer = <K extends keyof OrganizationalGenerationAnswers>(
    field: K,
    value: OrganizationalGenerationAnswers[K]
  ) => {
    setDraft((previous) => ({ ...previous, answers: { ...previous.answers, [field]: value } }));
    setError('');
  };
  const updateProposal = (change: (proposal: StructureProposal) => StructureProposal) => {
    if (draft.pendingApply || busy) return;
    setDraft((previous) =>
      previous.preview
        ? {
            ...previous,
            preview: { ...previous.preview, proposal: change(previous.preview.proposal) },
          }
        : previous
    );
    setError('');
  };
  const preview = draft.preview;
  const stale = !!preview && preview.baseRevision !== workspace.revision;
  const problems = preview ? proposalProblems(preview.proposal, workspace.snapshot) : [];
  const duplicates = preview ? proposalDuplicates(preview.proposal, workspace.snapshot) : [];
  const canContinue =
    !!draft.answers.primaryFocus &&
    !!draft.answers.companyStage &&
    !!draft.answers.geographicalScope;

  const generate = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy('preview');
    setError('');
    setConflict(false);
    setNote('');
    const token = ++previewToken.current;
    const controller = new AbortController();
    previewController.current = controller;
    const isCurrent = () =>
      alive.current && currentKey.current === key && previewToken.current === token;
    try {
      const result = await withSubscriptionGuard(async () => {
        try {
          const result = await organizationApi.preview(
            companyId,
            workspace.revision,
            draft.answers,
            controller.signal
          );
          return isCurrent() ? result : null;
        } catch (failure) {
          // Do not open a subscription modal or mutate a dismissed builder.
          if (!isCurrent()) return null;
          throw failure;
        }
      });
      if (!isCurrent()) return;
      if (result) {
        setContextConflict(false);
        setDraft((previous) => ({ ...previous, step: 2, preview: result, pendingApply: null }));
      }
    } catch (failure) {
      if (!isCurrent()) return;
      setConflict(failure instanceof OrganizationApiError && failure.status === 409);
      if (failure instanceof OrganizationApiError && failure.code === 'STALE_CONTEXT')
        setContextConflict(true);
      setError(
        failure instanceof Error
          ? failure.message
          : 'Suggestions could not be generated. Your answers are retained.'
      );
    } finally {
      if (isCurrent()) {
        previewController.current = null;
        inFlight.current = false;
        setBusy(null);
      }
    }
  };

  const close = () => {
    if (busy === 'apply') return;
    if (busy === 'preview') {
      previewToken.current += 1;
      previewController.current?.abort();
      previewController.current = null;
      inFlight.current = false;
      setBusy(null);
    }
    onClose();
  };

  const reviewRetainedDraft = () => {
    if (
      !preview ||
      busy ||
      contextConflict ||
      draft.pendingApply ||
      preview.baseRevision === workspace.revision
    )
      return;
    setDraft((previous) =>
      previous.preview
        ? {
            ...previous,
            step: 2,
            preview: { ...previous.preview, baseRevision: workspace.revision },
            pendingApply: null,
          }
        : previous
    );
    setConflict(false);
    setError('');
    setNote(
      'Your edits are retained against the refreshed structure. Review the relationships and any highlighted issues before adding them.'
    );
  };

  const apply = async () => {
    if (
      !preview ||
      inFlight.current ||
      (!draft.pendingApply &&
        (stale ||
          conflict ||
          contextConflict ||
          problems.length > 0 ||
          proposalCount(preview.proposal) === 0))
    )
      return;
    inFlight.current = true;
    const payload = draft.pendingApply || {
      operationId: crypto.randomUUID(),
      expectedRevision: preview.baseRevision,
      ...(preview.baseContextRevision !== undefined
        ? { expectedContextRevision: preview.baseContextRevision }
        : {}),
      proposal: preview.proposal,
    };
    setDraft((previous) => ({ ...previous, pendingApply: payload }));
    setBusy('apply');
    setError('');
    try {
      const result = await organizationApi.apply(companyId, payload);
      try {
        sessionStorage.removeItem(key);
      } catch {
        /* Optional draft cache. */
      }
      // A committed save stays successful even when the surrounding page cannot refresh.
      try {
        await onApplied(result);
      } catch (refreshError) {
        console.error('Structure saved; workspace refresh failed:', refreshError);
      }
      onClose();
    } catch (failure) {
      const apiError = failure instanceof OrganizationApiError ? failure : null;
      const rejected = !!apiError && apiError.status >= 400 && apiError.status < 500;
      if (rejected) setDraft((previous) => ({ ...previous, pendingApply: null }));
      setConflict(apiError?.status === 409);
      setContextConflict(apiError?.code === 'STALE_CONTEXT');
      setError(
        failure instanceof Error
          ? failure.message
          : 'Saving could not be confirmed. Retry the same reviewed changes.'
      );
    } finally {
      inFlight.current = false;
      setBusy(null);
    }
  };

  const remove = (kind: ProposalKind, id: string) => {
    updateProposal((current) => replaceProposalReference(current, kind, id, null));
    setNote(
      'Suggestion removed. Any items linked to it are now at company level; review their relationships before saving.'
    );
  };
  const duplicateControl = (kind: ProposalKind, id: string) => {
    const duplicate = duplicates.find((item) => item.kind === kind && item.id === id);
    return duplicate ? (
      <div className="org-warning">
        <span>
          Matches {duplicate.existing ? 'existing' : 'another suggested'} {duplicate.targetName}.
          Rename this item or{' '}
        </span>
        <button
          type="button"
          className="org-button org-button-secondary"
          onClick={() =>
            updateProposal((current) =>
              replaceProposalReference(current, kind, id, duplicate.targetId)
            )
          }
        >
          Use {duplicate.existing ? 'existing item' : 'first suggestion'}
        </button>
      </div>
    ) : null;
  };
  const proposal = preview?.proposal;
  const divisions = [...workspace.snapshot.divisions, ...(proposal?.divisions || [])];
  const teams = [...workspace.snapshot.teams, ...(proposal?.teams || [])];
  const positions = [...workspace.snapshot.positions, ...(proposal?.positions || [])];
  const changeItem = (kind: ProposalKind, id: string, fields: Record<string, unknown>) =>
    updateProposal((current) => ({
      ...current,
      [kind]: current[kind].map((item) => (item.id === id ? { ...item, ...fields } : item)),
    }));
  const readOnlyReview = !!busy || !!draft.pendingApply;
  const selectOptions = (
    items: Array<{ id: string; name?: string; title?: string }>,
    excludeId?: string
  ) =>
    items
      .filter((item) => item.id !== excludeId)
      .map((item) => (
        <option key={item.id} value={item.id}>
          {item.name || item.title}
        </option>
      ));

  return (
    <>
      <Dialog.Root
        open
        onOpenChange={(open) => {
          if (!open) close();
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="org-dialog-overlay" />
          <Dialog.Content
            className="org-dialog org-wizard"
            onEscapeKeyDown={(event) => {
              if (busy === 'apply') event.preventDefault();
            }}
            onInteractOutside={(event) => event.preventDefault()}
          >
            <div className="org-dialog-header">
              <div>
                <Dialog.Title className="org-dialog-title">Build your structure</Dialog.Title>
                <Dialog.Description className="org-dialog-description">
                  Generate suggestions, review the relationships, then add them to your current
                  structure.
                </Dialog.Description>
              </div>
              <button
                type="button"
                disabled={busy === 'apply'}
                className="org-icon-button"
                aria-label="Close structure builder"
                onClick={close}
              >
                <X size={18} />
              </button>
            </div>
            <ol className="org-wizard-steps" aria-label="Builder progress">
              {['Company', 'Needs', 'Review'].map((label, index) => (
                <li
                  key={label}
                  className={`org-wizard-step ${draft.step === index ? 'is-active' : draft.step > index ? 'is-complete' : ''}`}
                  aria-current={draft.step === index ? 'step' : undefined}
                >
                  <span>{draft.step > index ? <Check size={14} /> : index + 1}</span>
                  {label}
                </li>
              ))}
            </ol>
            <div className="org-wizard-body">
              {draft.step === 0 && (
                <>
                  <div className="org-note">
                    <strong>{company.name}</strong>
                    <p>
                      {[company.industry, company.size && `${company.size} company`]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                    {company.description && <p>{company.description}</p>}
                  </div>
                  <div className="org-field-grid">
                    <label className="org-field">
                      Primary focus
                      <select
                        value={draft.answers.primaryFocus}
                        onChange={(event) => answer('primaryFocus', event.target.value)}
                      >
                        <option value="">Choose a focus</option>
                        <option value="product">Products</option>
                        <option value="service">Services</option>
                        <option value="saas">Software platform</option>
                        <option value="consulting">Consulting</option>
                        <option value="marketplace">Marketplace</option>
                        <option value="ecommerce">Commerce</option>
                        <option value="other">Other</option>
                      </select>
                    </label>
                    <label className="org-field">
                      Company stage
                      <select
                        value={draft.answers.companyStage}
                        onChange={(event) => answer('companyStage', event.target.value)}
                      >
                        <option value="">Choose a stage</option>
                        <option value="startup">Startup / small team</option>
                        <option value="scale-up">Growing / scale-up</option>
                        <option value="enterprise">Established / enterprise</option>
                      </select>
                    </label>
                    <label className="org-field">
                      Geographic scope
                      <select
                        value={draft.answers.geographicalScope}
                        onChange={(event) => answer('geographicalScope', event.target.value)}
                      >
                        <option value="">Choose a scope</option>
                        <option value="local">One location</option>
                        <option value="national">National</option>
                        <option value="multi-region">Multiple regions / countries</option>
                      </select>
                    </label>
                  </div>
                </>
              )}
              {draft.step === 1 && (
                <>
                  <fieldset className="org-wizard-options" disabled={!!busy}>
                    <legend>Which functions do you need?</legend>
                    <p>
                      Choose what matters to your company. The suggestions can use your existing
                      divisions and teams.
                    </p>
                    <div className="org-choice-grid">
                      {functionalAreas.map(([value, label]) => (
                        <label
                          key={value}
                          className={`org-choice ${draft.answers.functionalAreas.includes(value) ? 'is-selected' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={draft.answers.functionalAreas.includes(value)}
                            onChange={(event) =>
                              answer(
                                'functionalAreas',
                                event.target.checked
                                  ? [...draft.answers.functionalAreas, value]
                                  : draft.answers.functionalAreas.filter((item) => item !== value)
                              )
                            }
                          />
                          {label}
                        </label>
                      ))}
                    </div>
                  </fieldset>
                  <div className="org-wizard-options">
                    <label className="org-choice">
                      <input
                        type="checkbox"
                        disabled={!!busy}
                        checked={draft.answers.projectBasedWork}
                        onChange={(event) => answer('projectBasedWork', event.target.checked)}
                      />
                      We work in dedicated project teams
                    </label>
                    <label className="org-choice">
                      <input
                        type="checkbox"
                        disabled={!!busy}
                        checked={draft.answers.regulatoryCompliance}
                        onChange={(event) => answer('regulatoryCompliance', event.target.checked)}
                      />
                      We need specialist regulatory or compliance support
                    </label>
                  </div>
                  <fieldset className="org-wizard-options" disabled={!!busy}>
                    <legend>
                      Customers <small>(optional)</small>
                    </legend>
                    <div className="org-choice-grid">
                      {customerSegments.map(([value, label]) => (
                        <label key={value} className="org-choice">
                          <input
                            type="checkbox"
                            checked={draft.answers.customerSegments.includes(value)}
                            onChange={(event) =>
                              answer(
                                'customerSegments',
                                event.target.checked
                                  ? [...draft.answers.customerSegments, value]
                                  : draft.answers.customerSegments.filter((item) => item !== value)
                              )
                            }
                          />
                          {label}
                        </label>
                      ))}
                    </div>
                  </fieldset>
                  <label className="org-field">
                    Anything else to consider? <small>(optional)</small>
                    <textarea
                      rows={3}
                      disabled={!!busy}
                      value={draft.answers.additionalContext}
                      onChange={(event) => answer('additionalContext', event.target.value)}
                      placeholder="Keep it lean, include regional teams, or describe an unusual reporting arrangement…"
                    />
                  </label>
                  <p className="org-note">
                    Suggestions are a draft. Nothing changes until you review and add them.
                  </p>
                </>
              )}
              {draft.step === 2 && preview && (
                <div className="org-wizard-review">
                  <div className="org-note">
                    <strong>{proposalCount(preview.proposal)} suggested additions</strong>
                    <p>{preview.reasoning}</p>
                    <p>
                      Existing structure is preserved. This save creates one version in your
                      history.
                    </p>
                  </div>
                  {(stale || conflict || contextConflict) && (
                    <div className="org-warning" role="status">
                      <p>
                        {contextConflict
                          ? 'Your company brief changed. Generate and review fresh suggestions using the updated brief before adding them.'
                          : 'Your current structure changed. Refresh the workspace, then review your retained draft or regenerate suggestions before adding them.'}
                      </p>
                      {contextConflict && (
                        <button
                          type="button"
                          className="org-button org-button-secondary"
                          disabled={!!busy}
                          onClick={() => void generate()}
                        >
                          Regenerate from updated brief
                        </button>
                      )}
                      {!contextConflict && onRefresh && (
                        <button
                          type="button"
                          className="org-button org-button-secondary"
                          disabled={!!busy}
                          onClick={() => {
                            void onRefresh();
                          }}
                        >
                          Refresh current structure
                        </button>
                      )}
                      {!contextConflict && (
                        <button
                          type="button"
                          className="org-button org-button-secondary"
                          disabled={
                            !!busy ||
                            !!draft.pendingApply ||
                            preview.baseRevision === workspace.revision
                          }
                          onClick={reviewRetainedDraft}
                        >
                          Review retained draft
                        </button>
                      )}
                    </div>
                  )}
                  {note && (
                    <p className="org-note" role="status">
                      {note}
                    </p>
                  )}
                  <fieldset disabled={readOnlyReview}>
                    <section className="org-proposal-section">
                      <h3>
                        Divisions <span>{proposal?.divisions.length}</span>
                      </h3>
                      {proposal?.divisions.map((item) => (
                        <article key={item.id} className="org-proposal-row">
                          <div className="org-proposal-fields">
                            <label className="org-field">
                              Division name
                              <input
                                aria-label={`Division name: ${item.name}`}
                                value={item.name}
                                onChange={(event) =>
                                  changeItem('divisions', item.id, { name: event.target.value })
                                }
                              />
                            </label>
                            <label className="org-field">
                              Parent division
                              <select
                                aria-label={`Parent division: ${item.name}`}
                                value={item.parentDivisionId || ''}
                                onChange={(event) =>
                                  changeItem('divisions', item.id, {
                                    parentDivisionId: event.target.value || null,
                                  })
                                }
                              >
                                <option value="">Company level</option>
                                {selectOptions(divisions, item.id)}
                              </select>
                            </label>
                            <label className="org-field">
                              Purpose
                              <textarea
                                rows={2}
                                aria-label={`Division purpose: ${item.name}`}
                                value={item.description || ''}
                                onChange={(event) =>
                                  changeItem('divisions', item.id, {
                                    description: event.target.value,
                                  })
                                }
                              />
                            </label>
                          </div>
                          <button
                            type="button"
                            className="org-icon-button"
                            aria-label={`Remove division ${item.name}`}
                            onClick={() => remove('divisions', item.id)}
                          >
                            <Trash2 size={16} />
                          </button>
                          {duplicateControl('divisions', item.id)}
                        </article>
                      ))}
                      {!proposal?.divisions.length && (
                        <p className="org-empty">No new divisions suggested.</p>
                      )}
                    </section>
                    <section className="org-proposal-section">
                      <h3>
                        Teams <span>{proposal?.teams.length}</span>
                      </h3>
                      {proposal?.teams.map((item) => (
                        <article key={item.id} className="org-proposal-row">
                          <div className="org-proposal-fields">
                            <label className="org-field">
                              Team name
                              <input
                                aria-label={`Team name: ${item.name}`}
                                value={item.name}
                                onChange={(event) =>
                                  changeItem('teams', item.id, { name: event.target.value })
                                }
                              />
                            </label>
                            <label className="org-field">
                              Division
                              <select
                                aria-label={`Team division: ${item.name}`}
                                value={item.divisionId || ''}
                                onChange={(event) => {
                                  const divisionId = event.target.value || null;
                                  updateProposal((current) => ({
                                    ...current,
                                    teams: current.teams.map((team) =>
                                      team.id === item.id
                                        ? { ...team, divisionId, parentTeamId: null }
                                        : team
                                    ),
                                    positions: current.positions.map((position) =>
                                      position.teamId === item.id
                                        ? { ...position, divisionId }
                                        : position
                                    ),
                                  }));
                                }}
                              >
                                <option value="">Company level</option>
                                {selectOptions(divisions)}
                              </select>
                            </label>
                            <label className="org-field">
                              Parent team
                              <select
                                aria-label={`Parent team: ${item.name}`}
                                value={item.parentTeamId || ''}
                                onChange={(event) => {
                                  const parent = teams.find(
                                    (team) => team.id === event.target.value
                                  );
                                  changeItem('teams', item.id, {
                                    parentTeamId: parent?.id || null,
                                    ...(parent ? { divisionId: parent.divisionId || null } : {}),
                                  });
                                }}
                              >
                                <option value="">No parent team</option>
                                {selectOptions(
                                  teams.filter(
                                    (team) => (team.divisionId || '') === (item.divisionId || '')
                                  ),
                                  item.id
                                )}
                              </select>
                            </label>
                            <label className="org-field">
                              Purpose
                              <textarea
                                rows={2}
                                aria-label={`Team purpose: ${item.name}`}
                                value={item.description || ''}
                                onChange={(event) =>
                                  changeItem('teams', item.id, { description: event.target.value })
                                }
                              />
                            </label>
                          </div>
                          <button
                            type="button"
                            className="org-icon-button"
                            aria-label={`Remove team ${item.name}`}
                            onClick={() => remove('teams', item.id)}
                          >
                            <Trash2 size={16} />
                          </button>
                          {duplicateControl('teams', item.id)}
                        </article>
                      ))}
                      {!proposal?.teams.length && (
                        <p className="org-empty">No new teams suggested.</p>
                      )}
                    </section>
                    <section className="org-proposal-section">
                      <h3>
                        Roles <span>{proposal?.positions.length}</span>
                      </h3>
                      {proposal?.positions.map((item) => (
                        <article key={item.id} className="org-proposal-row">
                          <div className="org-proposal-fields">
                            <label className="org-field">
                              Role title
                              <input
                                aria-label={`Role title: ${item.title}`}
                                value={item.title}
                                onChange={(event) =>
                                  changeItem('positions', item.id, { title: event.target.value })
                                }
                              />
                            </label>
                            <label className="org-field">
                              Reports to
                              <select
                                aria-label={`Reports to: ${item.title}`}
                                value={item.parentPositionId || ''}
                                onChange={(event) =>
                                  changeItem('positions', item.id, {
                                    parentPositionId: event.target.value || null,
                                  })
                                }
                              >
                                <option value="">No reporting manager</option>
                                {selectOptions(positions, item.id)}
                              </select>
                            </label>
                            <label className="org-field">
                              Division
                              <select
                                aria-label={`Role division: ${item.title}`}
                                value={item.divisionId || ''}
                                onChange={(event) =>
                                  changeItem('positions', item.id, {
                                    divisionId: event.target.value || null,
                                    teamId: null,
                                  })
                                }
                              >
                                <option value="">Company level</option>
                                {selectOptions(divisions)}
                              </select>
                            </label>
                            <label className="org-field">
                              Team
                              <select
                                aria-label={`Role team: ${item.title}`}
                                value={item.teamId || ''}
                                onChange={(event) => {
                                  const team = teams.find(
                                    (candidate) => candidate.id === event.target.value
                                  );
                                  changeItem('positions', item.id, {
                                    teamId: team?.id || null,
                                    ...(team ? { divisionId: team.divisionId || null } : {}),
                                  });
                                }}
                              >
                                <option value="">No team</option>
                                {selectOptions(
                                  teams.filter(
                                    (team) => (team.divisionId || '') === (item.divisionId || '')
                                  )
                                )}
                              </select>
                            </label>
                            <label className="org-field">
                              Purpose
                              <textarea
                                rows={2}
                                aria-label={`Role purpose: ${item.title}`}
                                value={item.description || ''}
                                onChange={(event) =>
                                  changeItem('positions', item.id, {
                                    description: event.target.value,
                                  })
                                }
                              />
                            </label>
                          </div>
                          <button
                            type="button"
                            className="org-icon-button"
                            aria-label={`Remove role ${item.title}`}
                            onClick={() => remove('positions', item.id)}
                          >
                            <Trash2 size={16} />
                          </button>
                          {duplicateControl('positions', item.id)}
                        </article>
                      ))}
                      {!proposal?.positions.length && (
                        <p className="org-empty">No new roles suggested.</p>
                      )}
                    </section>
                  </fieldset>
                  {problems.length > 0 && (
                    <div className="org-warning" role="status">
                      {problems.map((problem) => (
                        <p key={problem}>{problem}</p>
                      ))}
                    </div>
                  )}
                  {preview.recommendations.length > 0 && (
                    <details className="org-note">
                      <summary>Implementation guidance</summary>
                      <ul>
                        {preview.recommendations.map((recommendation, index) => (
                          <li key={index}>{recommendation}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              )}
              {conflict && !contextConflict && draft.step !== 2 && onRefresh && (
                <div className="org-warning">
                  <p>
                    The current structure changed. Refresh it before generating suggestions again.
                  </p>
                  <button
                    type="button"
                    className="org-button org-button-secondary"
                    disabled={!!busy}
                    onClick={() => {
                      void onRefresh();
                    }}
                  >
                    Refresh current structure
                  </button>
                </div>
              )}
              {error && (
                <p className="org-error" role="alert">
                  {error}
                </p>
              )}
              {draft.pendingApply && !busy && (
                <p className="org-warning" role="status">
                  Saving could not be confirmed. Retry this exact request to recover the saved
                  result safely.
                </p>
              )}
            </div>
            <div className="org-dialog-footer">
              <button
                type="button"
                className="org-button org-button-secondary"
                disabled={!!busy || !!draft.pendingApply || draft.step === 0}
                onClick={() => setDraft((previous) => ({ ...previous, step: previous.step - 1 }))}
              >
                <ChevronLeft size={16} />
                Back
              </button>
              <div>
                {draft.step === 2 && (
                  <button
                    type="button"
                    className="org-button org-button-secondary"
                    disabled={!!busy || !!draft.pendingApply}
                    onClick={generate}
                  >
                    Regenerate
                  </button>
                )}
                <button
                  type="button"
                  className="org-button org-button-secondary"
                  disabled={busy === 'apply'}
                  onClick={close}
                >
                  Close
                </button>
                {draft.step === 0 ? (
                  <button
                    type="button"
                    className="org-button org-button-primary"
                    disabled={!canContinue}
                    onClick={() => setDraft((previous) => ({ ...previous, step: 1 }))}
                  >
                    Continue
                    <ChevronRight size={16} />
                  </button>
                ) : draft.step === 1 ? (
                  <button
                    type="button"
                    className="org-button org-button-primary"
                    disabled={!!busy || !canContinue}
                    onClick={generate}
                  >
                    {busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                    {busy ? 'Generating…' : 'Generate suggestions'}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="org-button org-button-primary"
                    disabled={
                      !!busy ||
                      (!draft.pendingApply &&
                        (stale ||
                          conflict ||
                          contextConflict ||
                          problems.length > 0 ||
                          !preview ||
                          proposalCount(preview.proposal) === 0))
                    }
                    onClick={apply}
                  >
                    {busy === 'apply' && <Loader2 size={16} className="animate-spin" />}
                    {busy === 'apply'
                      ? 'Adding structure…'
                      : draft.pendingApply
                        ? 'Retry save'
                        : 'Add reviewed structure'}
                  </button>
                )}
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
      <UpgradeModal {...upgradeModalProps} />
    </>
  );
}
